// Draft Saver Utility for Product Forms
// Automatically saves form data to localStorage and restores on return

export class DraftSaver {
  constructor(formKey, autoSaveInterval = 5000) {
    this.formKey = formKey;
    this.autoSaveInterval = autoSaveInterval;
    this.autoSaveTimer = null;
    this.lastSavedData = null;
  }

  // Save form data to localStorage
  saveDraft(formData) {
    try {
      const draftData = {
        data: formData,
        timestamp: Date.now(),
        formKey: this.formKey
      };
      
      localStorage.setItem(`draft_${this.formKey}`, JSON.stringify(draftData));
      this.lastSavedData = JSON.stringify(formData);
      
      console.log(`Draft saved for ${this.formKey} at ${new Date().toLocaleTimeString()}`);
      return true;
    } catch (error) {
      console.error('Error saving draft:', error);
      return false;
    }
  }

  // Load draft data from localStorage
  loadDraft() {
    try {
      const draftString = localStorage.getItem(`draft_${this.formKey}`);
      if (!draftString) return null;
      
      const draftData = JSON.parse(draftString);
      
      // Check if draft is not too old (7 days)
      const isExpired = Date.now() - draftData.timestamp > 7 * 24 * 60 * 60 * 1000;
      if (isExpired) {
        this.clearDraft();
        return null;
      }
      
      console.log(`Draft loaded for ${this.formKey} from ${new Date(draftData.timestamp).toLocaleString()}`);
      return draftData.data;
    } catch (error) {
      console.error('Error loading draft:', error);
      return null;
    }
  }

  // Clear draft data
  clearDraft() {
    try {
      localStorage.removeItem(`draft_${this.formKey}`);
      this.lastSavedData = null;
      console.log(`Draft cleared for ${this.formKey}`);
      return true;
    } catch (error) {
      console.error('Error clearing draft:', error);
      return false;
    }
  }

  // Check if there are unsaved changes
  hasUnsavedChanges(currentData) {
    if (!this.lastSavedData) return false;
    
    try {
      const currentString = JSON.stringify(currentData);
      return currentString !== this.lastSavedData;
    } catch (error) {
      return false;
    }
  }

  // Start auto-save timer
  startAutoSave(formData, onSave) {
    this.stopAutoSave();
    
    this.autoSaveTimer = setInterval(() => {
      if (this.hasUnsavedChanges(formData)) {
        this.saveDraft(formData);
        if (onSave) onSave(formData);
        
        // Show warning banner when changes are detected
        this.showUnsavedChangesWarning();
      }
    }, this.autoSaveInterval);
  }

  // Stop auto-save timer
  stopAutoSave() {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
  }

  // Setup beforeunload event listener
  setupBeforeUnload(formData) {
    // Always save draft when there are unsaved changes
    const alwaysSaveOnUnload = () => {
      if (this.hasUnsavedChanges(formData)) {
        this.saveDraft(formData);
        console.log('Draft saved automatically on unload');
      }
    };

    // Add unload event listener that always saves
    window.addEventListener('unload', alwaysSaveOnUnload);
    
    // Setup the main beforeunload handler
    const cleanup = this.setupBeforeUnloadHandler(formData);
    
    // Return cleanup function that removes both listeners
    return () => {
      window.removeEventListener('unload', alwaysSaveOnUnload);
      cleanup();
    };
  }

  // Setup the main beforeunload handler
  setupBeforeUnloadHandler(formData) {
    let hasUnsavedChanges = false;
    
    // Check for unsaved changes and update flag
    const checkForChanges = () => {
      hasUnsavedChanges = this.hasUnsavedChanges(formData);
      return hasUnsavedChanges;
    };
    
    // Handle beforeunload event - this is the main event for window closing
    const handleBeforeUnload = (e) => {
      if (checkForChanges()) {
        // Show warning banner
        this.showUnsavedChangesWarning();
        
        // This will show the browser's default warning dialog
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Your progress will be saved as a draft.';
        return 'You have unsaved changes. Your progress will be saved as a draft.';
      }
    };

    // Handle pagehide event (more reliable than beforeunload)
    const handlePageHide = (e) => {
      if (checkForChanges()) {
        // Save draft automatically when page is being hidden
        this.saveDraft(formData);
        console.log('Draft saved automatically on page hide');
      }
    };

    // Handle visibility change (when tab becomes hidden)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && checkForChanges()) {
        // Save draft automatically when tab becomes hidden
        this.saveDraft(formData);
        console.log('Draft saved automatically on visibility change');
      }
    };

    // Handle unload event (final fallback)
    const handleUnload = () => {
      if (checkForChanges()) {
        this.saveDraft(formData);
        console.log('Draft saved automatically on unload');
      }
    };

    // Add event listeners with proper order
    window.addEventListener('beforeunload', handleBeforeUnload, { capture: true });
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('unload', handleUnload);
    
    // Return cleanup function
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload, { capture: true });
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('unload', handleUnload);
    };
  }

  // Manually show draft save popup (for navigation events)
  showDraftSavePopupManually(formData) {
    this.showDraftSavePopup(formData, false);
  }

  // Test method to manually trigger the popup
  testDraftSavePopup(formData) {
    console.log('Testing draft save popup...');
    this.showDraftSavePopup(formData, false);
  }

  // Show a visible warning banner when there are unsaved changes
  showUnsavedChangesWarning() {
    // Remove any existing warning
    const existingWarning = document.getElementById('unsaved-changes-warning');
    if (existingWarning) {
      existingWarning.remove();
    }

    // Create warning banner
    const warning = document.createElement('div');
    warning.id = 'unsaved-changes-warning';
    warning.innerHTML = `
      <div class="warning-content">
        <span class="warning-icon">⚠️</span>
        <span class="warning-text">You have unsaved changes. Your progress will be automatically saved as a draft.</span>
        <button class="warning-close" onclick="this.parentElement.parentElement.remove()">×</button>
      </div>
    `;

    // Add styles if not already present
    if (!document.getElementById('unsaved-changes-warning-styles')) {
      const styles = document.createElement('style');
      styles.id = 'unsaved-changes-warning-styles';
      styles.textContent = `
        #unsaved-changes-warning {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          background: #ff9800;
          color: white;
          z-index: 10001;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          animation: slideDown 0.3s ease;
        }
        
        .warning-content {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 12px 20px;
          gap: 12px;
          max-width: 1200px;
          margin: 0 auto;
        }
        
        .warning-icon {
          font-size: 1.2rem;
        }
        
        .warning-text {
          flex: 1;
          text-align: center;
          font-weight: 500;
        }
        
        .warning-close {
          background: none;
          border: none;
          color: white;
          font-size: 1.5rem;
          cursor: pointer;
          padding: 0;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: background-color 0.2s ease;
        }
        
        .warning-close:hover {
          background: rgba(255,255,255,0.2);
        }
        
        @keyframes slideDown {
          from { transform: translateY(-100%); }
          to { transform: translateY(0); }
        }
        
        @media (max-width: 768px) {
          .warning-content {
            padding: 10px 16px;
            gap: 8px;
          }
          
          .warning-text {
            font-size: 0.9rem;
          }
        }
      `;
      document.head.appendChild(styles);
    }

    // Add warning to page
    document.body.appendChild(warning);

    // Auto-remove after 10 seconds
    setTimeout(() => {
      if (document.body.contains(warning)) {
        warning.remove();
      }
    }, 10000);
  }

  // Show custom draft save popup
  showDraftSavePopup(formData, isBeforeUnload = false) {
    // Create popup container
    const popup = document.createElement('div');
    popup.className = 'draft-save-popup-overlay';
    popup.innerHTML = `
      <div class="draft-save-popup">
        <div class="popup-header">
          <h3>📝 Save Your Progress?</h3>
          <p>You have unsaved changes. Would you like to save your work as a draft?</p>
        </div>
        <div class="popup-actions">
          <button class="popup-btn save-draft-btn">
            💾 Save as Draft
          </button>
          <button class="popup-btn discard-btn">
            ❌ Discard Changes
          </button>
          <button class="popup-btn cancel-btn">
            🔄 Continue Editing
          </button>
        </div>
        <div class="popup-footer">
          <small>💡 Tip: Try closing this tab to test the draft saving!</small>
        </div>
      </div>
    `;

    // Add styles if not already present
    if (!document.getElementById('draft-save-popup-styles')) {
      const styles = document.createElement('style');
      styles.id = 'draft-save-popup-styles';
      styles.textContent = `
        .draft-save-popup-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          animation: fadeIn 0.3s ease;
        }
        
        .draft-save-popup {
          background: white;
          border-radius: 12px;
          padding: 30px;
          max-width: 450px;
          width: 90%;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
          animation: slideIn 0.3s ease;
        }
        
        .popup-header h3 {
          margin: 0 0 15px 0;
          color: #1976d2;
          font-size: 1.4rem;
          text-align: center;
        }
        
        .popup-header p {
          margin: 0 0 25px 0;
          color: #666;
          text-align: center;
          line-height: 1.5;
        }
        
        .popup-actions {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        
        .popup-footer {
          margin-top: 20px;
          text-align: center;
          padding-top: 15px;
          border-top: 1px solid #eee;
        }
        
        .popup-footer small {
          color: #888;
          font-size: 0.85rem;
          font-style: italic;
        }
        
        .popup-btn {
          padding: 14px 20px;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: center;
        }
        
        .save-draft-btn {
          background: #1976d2;
          color: white;
        }
        
        .save-draft-btn:hover {
          background: #1565c0;
          transform: translateY(-2px);
        }
        
        .discard-btn {
          background: #f44336;
          color: white;
        }
        
        .discard-btn:hover {
          background: #d32f2f;
          transform: translateY(-2px);
        }
        
        .cancel-btn {
          background: #6c757d;
          color: white;
        }
        
        .cancel-btn:hover {
          background: #5a6268;
          transform: translateY(-2px);
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideIn {
          from { transform: translateY(-20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        @media (max-width: 480px) {
          .draft-save-popup {
            padding: 20px;
            margin: 20px;
          }
          
          .popup-header h3 {
            font-size: 1.2rem;
          }
          
          .popup-btn {
            padding: 12px 16px;
            font-size: 0.9rem;
          }
        }
      `;
      document.head.appendChild(styles);
    }

    // Add popup to page
    document.body.appendChild(popup);

    // Handle save draft button click
    const saveDraftBtn = popup.querySelector('.save-draft-btn');
    saveDraftBtn.addEventListener('click', () => {
      this.saveDraft(formData);
      popup.remove();
      if (isBeforeUnload) {
        // Allow the page to unload after saving
        console.log('Draft saved, allowing page to unload');
      }
    });

    // Handle discard button click
    const discardBtn = popup.querySelector('.discard-btn');
    discardBtn.addEventListener('click', () => {
      this.clearDraft();
      popup.remove();
      if (isBeforeUnload) {
        // Allow the page to unload without saving
        console.log('Draft discarded, allowing page to unload');
      }
    });

    // Handle cancel button click
    const cancelBtn = popup.querySelector('.cancel-btn');
    cancelBtn.addEventListener('click', () => {
      popup.remove();
      // Keep the page open and continue editing
    });

    // Auto-remove popup after 10 seconds if user doesn't interact
    setTimeout(() => {
      if (document.body.contains(popup)) {
        popup.remove();
      }
    }, 10000);
  }

  // Get draft info (for UI display)
  getDraftInfo() {
    try {
      const draftString = localStorage.getItem(`draft_${this.formKey}`);
      if (!draftString) return null;
      
      const draftData = JSON.parse(draftString);
      const isExpired = Date.now() - draftData.timestamp > 7 * 24 * 60 * 60 * 1000;
      
      return {
        exists: !isExpired,
        timestamp: draftData.timestamp,
        age: Date.now() - draftData.timestamp,
        ageText: this.formatAge(draftData.timestamp)
      };
    } catch (error) {
      return null;
    }
  }

  // Format age in human-readable format
  formatAge(timestamp) {
    const age = Date.now() - timestamp;
    const minutes = Math.floor(age / (1000 * 60));
    const hours = Math.floor(age / (1000 * 60 * 60));
    const days = Math.floor(age / (1000 * 60 * 60 * 24));
    
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    return 'Just now';
  }
}

// React Hook for using DraftSaver
export const useDraftSaver = (formKey, initialData = {}, autoSaveInterval = 5000) => {
  // This hook should be used in React components
  // The DraftSaver class can be used directly for non-React usage
  throw new Error('useDraftSaver hook is not yet implemented. Use DraftSaver class directly.');
};
